import {auth, db} from './firebase';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    linkWithCredential,
    AuthCredential,
    User as FirebaseUser
} from 'firebase/auth';
import {
    doc,
    setDoc,
    deleteDoc,
    collection,
    getDocs
} from 'firebase/firestore';
import {User, UserRole} from '../types';
import { whoami, reportLogin, adminSession, markAdminBrowser } from './extras';

const USERS_COLLECTION = 'user_roles';
type Role = "user" | "administrator" | "superadministrator";

// Google účet se stejným e-mailem jako stávající účet s heslem: Firebase vyžaduje jednorázové propojení.
let pendingGoogleCredential: AuthCredential | null = null;
let lastCallback: ((user: User | null) => void) | null = null;

async function resolveUser(firebaseUser: FirebaseUser): Promise<User> {
    const tokenResult = await firebaseUser.getIdTokenResult();
    const roleClaim = (tokenResult.claims.role as Role | undefined) ?? "user";
    const base: User = { uid: firebaseUser.uid, email: firebaseUser.email || '', role: roleClaim };
    if (roleClaim !== 'administrator' && roleClaim !== 'superadministrator') return base;
    markAdminBrowser();
    try {
        let me = await whoami();
        // Server právě zapsal potvrzení 2FA do tokenu – bez nového tokenu by Firestore zápis odmítl.
        if (me.refreshToken) { await firebaseUser.getIdToken(true); me = await whoami(); }
        const t = me.twofa;
        if (t && t.enabled && !t.verified) return { ...base, role: 'user', adminRole: roleClaim, twofaPending: 'verify', twofaEnabled: true };
        if (t && t.required && !t.enabled) return { ...base, role: 'user', adminRole: roleClaim, twofaPending: 'setup', twofaEnabled: false };
        return { ...base, adminRole: roleClaim, twofaEnabled: !!t?.enabled };
    } catch (e) {
        console.error('[2FA] Stav ověření se nepodařilo zjistit:', e);
        // Bez odpovědi serveru administraci neodemykáme (bezpečnější varianta)
        return { ...base, role: 'user', adminRole: roleClaim, twofaPending: 'error' };
    }
}

export const authService = {
    isConfigured: !!auth && !!db,
    login: async (email: string, password: string): Promise<void> => {
        if (!auth) throw new Error("Firebase není nakonfigurován.");
        try {
            const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
            if (pendingGoogleCredential) {
                try { await linkWithCredential(cred.user, pendingGoogleCredential); } catch (e) { console.warn('[Google] Propojení selhalo:', e); }
                pendingGoogleCredential = null;
            }
            reportLogin(true, 'password');
        } catch (e: any) {
            reportLogin(false, 'password', email.trim(), e?.code);
            throw e;
        }
    },
    // Přihlášení Google účtem (vyžaduje zapnutého poskytovatele Google ve Firebase a povolenou doménu)
    loginWithGoogle: async (): Promise<'ok' | 'link-needed'> => {
        if (!auth) throw new Error("Firebase není nakonfigurován.");
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        try {
            const cred = await signInWithPopup(auth, provider);
            const t = await cred.user.getIdTokenResult(true);
            const role = t.claims.role as Role | undefined;
            if (role !== 'administrator' && role !== 'superadministrator') {
                reportLogin(false, 'google', cred.user.email || '', 'bez-opravneni');
                await signOut(auth);
                throw Object.assign(new Error('Tento Google účet nemá přístup do administrace.'), { code: 'app/no-role' });
            }
            reportLogin(true, 'google');
            return 'ok';
        } catch (e: any) {
            if (e?.code === 'auth/account-exists-with-different-credential') {
                pendingGoogleCredential = GoogleAuthProvider.credentialFromError(e);
                return 'link-needed';
            }
            if (e?.code !== 'app/no-role' && e?.code !== 'auth/popup-closed-by-user') reportLogin(false, 'google', e?.customData?.email, e?.code);
            throw e;
        }
    },
    register: async (email: string, password: string): Promise<void> => {
        if (!auth) throw new Error("Firebase není nakonfigurován.");
        await createUserWithEmailAndPassword(auth, email.trim(), password);
    },
    logout: async (): Promise<void> => {
        if (!auth) return;
        try {
            const u = auth.currentUser;
            if (u) await fetch('/api/admin/logout', { method: 'POST', headers: { Authorization: `Bearer ${await u.getIdToken()}` } });
        } catch { /* odhlášení nesmí selhat */ }
        adminSession.clear();
        await signOut(auth);
    },
    // Po ověření kódem 2FA znovu vyhodnotí přihlášeného uživatele
    recheck: async (): Promise<void> => {
        if (!auth?.currentUser || !lastCallback) return;
        lastCallback(await resolveUser(auth.currentUser));
    },
    onAuthStateChange: (callback: (user: User | null) => void) => {
        if (!auth || !db) {
            callback(null);
            return () => {
            };
        }
        lastCallback = callback;
        return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser && firebaseUser.email) {
                callback(await resolveUser(firebaseUser));
            } else {
                callback(null);
            }
        });
    },
    getUsers: async (): Promise<User[]> => {
        if (!db) return [];
        const snapshot = await getDocs(collection(db, USERS_COLLECTION));
        return snapshot.docs.map(doc => ({
            uid: doc.id,
            email: doc.id,
            role: (doc.data().role as UserRole) || 'user'
        }));
    },

    addUserRole: async (email: string, role: UserRole): Promise<void> => {
        if (!db) throw new Error("DB nedostupná.");
        await setDoc(doc(db, USERS_COLLECTION, email.trim().toLowerCase()), {role});
    },

    removeUserRole: async (email: string): Promise<void> => {
        if (!db) throw new Error("DB nedostupná.");
        await deleteDoc(doc(db, USERS_COLLECTION, email.trim().toLowerCase()));
    }
};
